import { useId } from "react"

const MONTHS = ["J", "F", "M", "A", "M", "J", "J", "A", "S", "O", "N", "D"] as const
const CURRENT_MONTH_INDEX = 4 // May
const SCHEDULED_MONTHS = new Set([3, 5]) // April, June

const PRIORITY_COLORS = {
  high: "#ef4444",
  medium: "#f59e0b",
  low: "#22c55e",
} as const

type TaskCategory = "mow" | "fertilize" | "aerate"

interface TaskRow {
  priority: keyof typeof PRIORITY_COLORS
  label: string
  category: TaskCategory
  done?: boolean
}

const TASKS: readonly TaskRow[] = [
  { priority: "low", label: "First mow of season", category: "mow", done: true },
  { priority: "medium", label: "Apply pre-emergent (early April)", category: "fertilize" },
  { priority: "high", label: "Aerate cool-season turf", category: "aerate" },
] as const

function CategoryIcon({ category, x, y }: { category: TaskCategory; x: number; y: number }) {
  const stroke = "#9ca3af"
  return (
    <g
      transform={`translate(${x}, ${y})`}
      stroke={stroke}
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    >
      {category === "mow" && (
        <>
          <circle cx="3.5" cy="9" r="2" />
          <circle cx="3.5" cy="3" r="2" />
          <path d="M5 4.4 L12 11" />
          <path d="M5 7.6 L12 1" />
        </>
      )}
      {category === "fertilize" && (
        <>
          <path d="M7 1 L12 3 V7 C12 9.5, 10 11.4, 7 13 C4 11.4, 2 9.5, 2 7 V3 Z" />
          <path d="M5 7 L6.5 8.5 L9 5.5" />
        </>
      )}
      {category === "aerate" && (
        <>
          <circle cx="3" cy="3" r="0.9" fill={stroke} stroke="none" />
          <circle cx="7" cy="3" r="0.9" fill={stroke} stroke="none" />
          <circle cx="11" cy="3" r="0.9" fill={stroke} stroke="none" />
          <circle cx="3" cy="7" r="0.9" fill={stroke} stroke="none" />
          <circle cx="7" cy="7" r="0.9" fill={stroke} stroke="none" />
          <circle cx="11" cy="7" r="0.9" fill={stroke} stroke="none" />
          <circle cx="3" cy="11" r="0.9" fill={stroke} stroke="none" />
          <circle cx="7" cy="11" r="0.9" fill={stroke} stroke="none" />
          <circle cx="11" cy="11" r="0.9" fill={stroke} stroke="none" />
        </>
      )}
    </g>
  )
}

export function CarePlanCardSvg({ compact = false }: { compact?: boolean }) {
  const idPrefix = `care-plan-${useId().replace(/:/g, "")}`
  const ids = {
    shadow: `${idPrefix}-shadow`,
    cardBackground: `${idPrefix}-card-background`,
    ambientGlow: `${idPrefix}-ambient-glow`,
    bladeLight: `${idPrefix}-blade-light`,
    bladeDark: `${idPrefix}-blade-dark`,
  }

  return (
    <svg
      viewBox={compact ? "20 0 440 330" : "0 0 480 340"}
      role="img"
      aria-label="Sample 12-month lawn care plan card"
      className="w-full h-auto"
    >
      <defs>
        <filter id={ids.shadow} x="-10%" y="-10%" width="120%" height="135%">
          <feGaussianBlur in="SourceAlpha" stdDeviation="6" />
          <feOffset dx="0" dy="6" result="offsetblur" />
          <feComponentTransfer>
            <feFuncA type="linear" slope="0.10" />
          </feComponentTransfer>
          <feMerge>
            <feMergeNode />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <linearGradient id={ids.cardBackground} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="100%" stopColor="#f9fafb" />
        </linearGradient>
        <radialGradient id={ids.ambientGlow} cx="50%" cy="55%" r="55%">
          <stop offset="0%" stopColor="#bbf7d0" stopOpacity="0.55" />
          <stop offset="60%" stopColor="#dcfce7" stopOpacity="0.25" />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
        </radialGradient>
        <linearGradient id={ids.bladeLight} x1="0" y1="1" x2="0" y2="0">
          <stop offset="0%" stopColor="#16a34a" />
          <stop offset="100%" stopColor="#86efac" />
        </linearGradient>
        <linearGradient id={ids.bladeDark} x1="0" y1="1" x2="0" y2="0">
          <stop offset="0%" stopColor="#14532d" />
          <stop offset="100%" stopColor="#22c55e" />
        </linearGradient>
      </defs>

      {/* Ambient glow behind everything */}
      <ellipse cx="240" cy="180" rx="220" ry="150" fill={`url(#${ids.ambientGlow})`} />

      {/* Sun-ray accent — three soft strokes top-right */}
      <g
        stroke="#fde68a"
        strokeWidth="2.2"
        strokeLinecap="round"
        opacity="0.75"
      >
        <line x1="430" y1="38" x2="446" y2="22" />
        <line x1="442" y1="58" x2="462" y2="50" />
        <line x1="416" y1="22" x2="422" y2="6" />
      </g>
      <circle cx="442" cy="34" r="6" fill="#fde68a" opacity="0.85" />

      {/* Grass blades behind the card */}
      <g>
        <path
          d="M 60 308 C 62 280, 56 250, 52 234"
          stroke={`url(#${ids.bladeDark})`}
          strokeWidth="3"
          strokeLinecap="round"
          fill="none"
        />
        <path
          d="M 78 312 C 80 285, 84 258, 90 240"
          stroke={`url(#${ids.bladeLight})`}
          strokeWidth="2.4"
          strokeLinecap="round"
          fill="none"
        />
        <path
          d="M 410 312 C 408 286, 414 256, 422 238"
          stroke={`url(#${ids.bladeLight})`}
          strokeWidth="2.4"
          strokeLinecap="round"
          fill="none"
        />
        <path
          d="M 432 314 C 432 288, 426 258, 420 244"
          stroke={`url(#${ids.bladeDark})`}
          strokeWidth="3"
          strokeLinecap="round"
          fill="none"
        />
      </g>

      {/* Card — tilted slightly for life. Group-rotated around its center. */}
      <g transform="rotate(-1.4 240 172)">
        <rect
          x="52"
          y="44"
          width="376"
          height="256"
          rx="18"
          fill={`url(#${ids.cardBackground})`}
          stroke="#dcfce7"
          strokeWidth="1.5"
          filter={`url(#${ids.shadow})`}
        />

        {/* Header row */}
        <g transform="translate(68, 62)">
          <circle cx="10" cy="10" r="14" fill="#dcfce7" />
          <g
            transform="translate(2, 1)"
            stroke="#15803d"
            strokeWidth="1.7"
            strokeLinecap="round"
            fill="none"
          >
            <path d="M3 18 C3 13, 1.5 9.5, 0.5 7" />
            <path d="M9 18 C9 11, 8 7, 8 3" />
            <path d="M15 18 C15 13, 16.5 9.5, 17.5 7" />
          </g>

          <text
            x="34"
            y="15"
            fill="#0f172a"
            fontSize="13"
            fontWeight="700"
            fontFamily="system-ui, -apple-system, Segoe UI, sans-serif"
          >
            12-month care plan
          </text>
          {/* Brand-tie squiggle under the title */}
          <path
            d="M34 22 C 70 19, 130 25, 168 21"
            stroke="#16a34a"
            strokeWidth="1.6"
            strokeLinecap="round"
            fill="none"
            opacity="0.55"
          />

          {/* Season pill */}
          <g transform="translate(280, 1)">
            <rect width="64" height="20" rx="10" fill="#dcfce7" />
            <circle cx="13" cy="10" r="3" fill="#16a34a" />
            <text
              x="38"
              y="14"
              textAnchor="middle"
              fill="#15803d"
              fontSize="11"
              fontWeight="600"
              fontFamily="system-ui, -apple-system, Segoe UI, sans-serif"
            >
              Spring
            </text>
          </g>
        </g>

        {/* Month strip */}
        <g transform="translate(68, 104)">
          {MONTHS.map((letter, i) => {
            const x = i * 29
            const isCurrent = i === CURRENT_MONTH_INDEX
            const hasDot = SCHEDULED_MONTHS.has(i)
            return (
              <g key={i} transform={`translate(${x}, 0)`}>
                {hasDot && !isCurrent && (
                  <>
                    <circle cx="13" cy="-5" r="2.5" fill="#16a34a" />
                    <line
                      x1="13"
                      y1="-2"
                      x2="13"
                      y2="1"
                      stroke="#16a34a"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </>
                )}
                <rect
                  width="26"
                  height="30"
                  rx="6"
                  fill={isCurrent ? "#dcfce7" : "#f3f4f6"}
                  stroke={isCurrent ? "#86efac" : "none"}
                  strokeWidth={isCurrent ? "1" : "0"}
                />
                {isCurrent && (
                  <rect
                    width="26"
                    height="10"
                    rx="6"
                    fill="#ffffff"
                    opacity="0.18"
                  />
                )}
                <text
                  x="13"
                  y="20"
                  textAnchor="middle"
                  fill={isCurrent ? "#15803d" : "#6b7280"}
                  fontSize="11"
                  fontWeight={isCurrent ? "700" : "600"}
                  fontFamily="system-ui, -apple-system, Segoe UI, sans-serif"
                >
                  {letter}
                </text>
              </g>
            )
          })}
        </g>

        {/* Divider */}
        <line x1="68" y1="150" x2="412" y2="150" stroke="#f3f4f6" strokeWidth="1" />

        {/* Task rows */}
        <g transform="translate(68, 162)">
          {TASKS.map((task, i) => {
            const y = i * 30
            const textFill = task.done ? "#9ca3af" : "#374151"
            return (
              <g key={i} transform={`translate(0, ${y})`}>
                <circle cx="6" cy="10" r="5" fill={PRIORITY_COLORS[task.priority]} />
                <text
                  x="20"
                  y="14"
                  fill={textFill}
                  fontSize="11"
                  fontWeight="500"
                  fontFamily="system-ui, -apple-system, Segoe UI, sans-serif"
                  textDecoration={task.done ? "line-through" : undefined}
                >
                  {task.label}
                </text>
                <CategoryIcon category={task.category} x={324} y={3} />
                {task.done && (
                  <g transform="translate(304, 4)">
                    <circle cx="6" cy="6" r="6" fill="#dcfce7" />
                    <path
                      d="M3.5 6.2 L5.2 8 L8.7 4.5"
                      stroke="#16a34a"
                      strokeWidth="1.7"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      fill="none"
                    />
                  </g>
                )}
              </g>
            )
          })}
        </g>

        {/* Footer microline */}
        <g transform="translate(68, 278)">
          <text
            fill="#9ca3af"
            fontSize="10"
            fontFamily="system-ui, -apple-system, Segoe UI, sans-serif"
          >
            Tuned to ZIP 53703 · Kentucky Bluegrass
          </text>
        </g>
      </g>

      {/* Foreground grass blades — in front of the card edges */}
      <g>
        <path
          d="M 38 320 C 40 290, 34 258, 28 240"
          stroke={`url(#${ids.bladeLight})`}
          strokeWidth="2.6"
          strokeLinecap="round"
          fill="none"
        />
        <path
          d="M 96 322 C 98 296, 104 268, 110 252"
          stroke={`url(#${ids.bladeDark})`}
          strokeWidth="2.4"
          strokeLinecap="round"
          fill="none"
        />
        <path
          d="M 388 320 C 386 294, 392 264, 400 248"
          stroke={`url(#${ids.bladeDark})`}
          strokeWidth="2.4"
          strokeLinecap="round"
          fill="none"
        />
        <path
          d="M 452 322 C 452 294, 446 262, 440 246"
          stroke={`url(#${ids.bladeLight})`}
          strokeWidth="2.6"
          strokeLinecap="round"
          fill="none"
        />
      </g>

    </svg>
  )
}
